<script setup>
import { computed, ref, watch } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';
import { useAlert } from 'dashboard/composables';
import equipesApi from 'dashboard/api/aceleres/equipes';
import { visibleTeamInboxes, teamInboxStatus } from '../helpers/inboxStatus';
import {
  isTeamRotationEnabled,
  isTeamSupervisor,
} from '../helpers/memberRoles';
import {
  getInboxIconByType,
  isWhatsAppStyleInbox,
  INBOX_TYPES,
} from 'dashboard/helper/inbox';

const props = defineProps({
  team: { type: Object, default: null },
  members: { type: Array, default: () => [] },
  accessLevel: { type: String, default: 'team' },
  inboxOptions: { type: Array, default: () => [] },
});

const emit = defineEmits([
  'updateInboxes',
  'updateDistributionMode',
  'updateAccessLevel',
  'updateIndividualCompanyStart',
  'openIa',
  'openMembers',
  'delete',
]);

const WHATSAPP_ICON_COLOR = 'text-[#25D366]';
const DEFAULT_CHANNEL_ICON_COLOR = 'text-[#16A34A]';

const CHANNEL_ICON_COLORS = {
  [INBOX_TYPES.WHATSAPP]: WHATSAPP_ICON_COLOR,
  [INBOX_TYPES.INSTAGRAM]: 'text-[#E1306C]',
  [INBOX_TYPES.FB]: 'text-[#0084FF]',
  [INBOX_TYPES.TELEGRAM]: 'text-[#229ED9]',
  [INBOX_TYPES.TWITTER]: 'text-[#0F172A]',
  [INBOX_TYPES.LINE]: 'text-[#06C755]',
  [INBOX_TYPES.TIKTOK]: 'text-[#0F172A]',
  [INBOX_TYPES.EMAIL]: 'text-[#EA4335]',
  [INBOX_TYPES.SMS]: 'text-[#0EA5E9]',
  [INBOX_TYPES.VOICE]: 'text-[#0EA5E9]',
};

// O payload da equipe agora traz channel_type/medium (lib/aceleres/team_payload.rb).
// No Aceleres o WhatsApp trafega como Channel::Api, entao isWhatsAppStyleInbox
// (mesma regra usada no ConversationBox) vem antes do mapa padrao — senao esses
// canais sairiam com o icone de nuvem do Channel::Api.
// Compatibilidade: enquanto o Puma nao for reiniciado, o payload vem sem
// channel_type. Nesse caso mantemos WhatsApp (o valor cravado ate agora) para
// nao regredir para o balao generico durante a janela de rollout.
const hasChannelType = inbox => Boolean(inbox?.channel_type);

const channelIcon = inbox => {
  if (!hasChannelType(inbox) || isWhatsAppStyleInbox(inbox)) {
    return getInboxIconByType(INBOX_TYPES.WHATSAPP);
  }
  return getInboxIconByType(inbox?.channel_type, inbox?.medium);
};

const channelIconColor = inbox => {
  if (!hasChannelType(inbox) || isWhatsAppStyleInbox(inbox)) {
    return WHATSAPP_ICON_COLOR;
  }
  return CHANNEL_ICON_COLORS[inbox?.channel_type] || DEFAULT_CHANNEL_ICON_COLOR;
};

const formatUnit = (count, singular, plural) =>
  `${count} ${count === 1 ? singular : plural}`;

const teamMetaLine = computed(() => {
  const channelCount = visibleTeamInboxes(props.team?.inboxes).length;
  const memberCount = props.members?.length || props.team?.member_count || 0;
  return [
    props.team?.name,
    formatUnit(channelCount, 'canal', 'canais'),
    formatUnit(memberCount, 'membro', 'membros'),
  ]
    .filter(Boolean)
    .join(' • ');
});

const showChannelPicker = ref(false);
const selectedInboxIds = ref([]);
const channelSearchQuery = ref('');
const activeTab = ref('channels');
const advancedDeleteConfirmed = ref(false);
const reminderLoading = ref(false);
const reminderSaving = ref(false);
const reminderPresets = ref({});
const reminderSettings = ref({
  enabled: false,
  message_source: 'preset',
  preset_key: 'preset_1',
  custom_message_index: 0,
  custom_messages: ['', '', ''],
  lead_minutes: 15,
  attempts: 3,
  interval_minutes: 5,
});

const reminderPresetLabels = {
  preset_1: 'Modelo 1',
  preset_2: 'Modelo 2',
  preset_3: 'Modelo 3',
  preset_4: 'Modelo 4',
  preset_5: 'Modelo 5',
};

const workspaceText = {
  setupTitle: 'Configurações da equipe',
  tipLabel: 'Dica:',
  tipChannels:
    'os canais adicionados aqui serão utilizados para receber e enviar conversas desta equipe.',
  setupDescription:
    'Defina como os atendimentos chegam, são distribuídos e gerenciados pela sua equipe.',
  tabsAriaLabel: 'Abas de configuração da equipe',
  stepLabel: 'ETAPA',
  ofLabel: 'DE',
  configuredLabel: 'Configurado',
  pendingLabel: 'Pendente',
  reviewLabel: 'Revisar',
  nextStep: 'Próxima aba',
  channelsTab: 'Canais',
  channelsTabDescription:
    'Defina por quais caixas de entrada esta equipe receberá conversas.',
  channelsStepperDescription: ['De onde chegam', 'os atendimentos'],
  rulesTab: 'Distribuição',
  rulesTabDescription:
    'Controle a distribuição dos leads e a visibilidade das conversas.',
  rulesStepperDescription: ['Como os leads serão', 'atribuídos e visualizados'],
  peopleTab: 'Equipe',
  peopleTabDescription: 'Organize quem atende e quem supervisiona esta equipe.',
  peopleStepperDescription: ['Quem participa', 'da equipe'],
  automationTab: 'Automação',
  automationTabDescription:
    'Centralize as configurações de IA, rodízio e horários desta equipe.',
  automationStepperDescription: ['IA, rodízio e regras', 'automáticas'],
  metaWindowTab: 'Janela do Instagram',
  metaWindowTabDescription: 'Mensagem preventiva antes do limite de 24 horas.',
  metaWindowStepperDescription: ['Mensagem antes da', 'janela e retentativas'],
  advancedTab: 'Avançado',
  advancedTabDescription: 'Ações sensíveis da equipe, como exclusão do grupo.',
  advancedStepperDescription: ['Permissões e', 'configurações extras'],
  whyItMatters: 'Por que esta etapa é importante?',
  channelsGuidance:
    'Sem canais vinculados, a equipe existe, mas não recebe conversas. Use esta aba para conectar ou remover caixas de entrada com segurança.',
  rulesGuidance:
    'A distribuição decide para qual atendente o lead será enviado; o acesso decide quem consegue visualizar a conversa depois. As duas regras precisam estar coerentes.',
  peopleGuidance:
    'Atendentes recebem e respondem conversas. Supervisores acompanham a operação e precisam enxergar o contexto completo da equipe.',
  automationGuidance:
    'Mesmo quando a IA não estiver ativa, rodízios e horários podem influenciar a chegada dos leads. Revise antes de considerar a equipe pronta.',
  advancedGuidance:
    'Excluir uma equipe é uma ação administrativa. Antes de remover, confirme se não há canais, usuários ou regras que ainda dependem deste grupo.',
  reviewChecklistTitle: 'O que revisar nesta aba',
  channelsChecklist: [
    'Confirme se os canais corretos estão vinculados à equipe.',
    'Remova caixas de entrada que não devem mais enviar conversas para este grupo.',
    'Use a busca para localizar rapidamente canais disponíveis.',
  ],
  rulesChecklist: [
    'Escolha se a distribuição deve priorizar a equipe ou o atendente atual.',
    'Defina quem pode enxergar atendimentos em andamento e concluídos.',
    'Use regras mais restritas quando a conversa tiver dados sensíveis.',
  ],
  peopleChecklist: [
    'Inclua como atendentes apenas usuários que devem receber conversas.',
    'Inclua supervisores para acompanhamento operacional da equipe.',
    'Revise a lista quando alguém mudar de função ou sair da operação.',
  ],
  automationChecklist: [
    'Abra o gerenciamento para revisar IA, horários e rodízios.',
    'Confirme se a equipe pode receber leads no horário esperado.',
    'Evite deixar automações antigas ativas sem revisão.',
  ],
  metaWindowTitle: 'Mensagem preventiva da janela de 24 horas',
  metaWindowDescription:
    'Envie uma mensagem antes de a janela do Instagram encerrar para estimular uma nova resposta do cliente.',
  metaWindowEnable: 'Ativar mensagem preventiva para esta equipe',
  metaWindowWarningTitle: 'Atenção: esta mensagem pode falhar',
  metaWindowWarning:
    'É sempre possível que o envio falhe. Se as retentativas não chegarem ao cliente logo e você configurar o primeiro envio para 5 minutos antes do encerramento, a mensagem pode não chegar a tempo. O organizador deve considerar essa margem de segurança ao configurar a regra.',
  metaWindowMessageTitle: 'Mensagem a enviar',
  metaWindowPresetOption: 'Mensagens sugeridas pelo sistema',
  metaWindowCustomOption: 'Personalizar mensagem',
  metaWindowCustomHint: 'Você pode salvar até 3 mensagens personalizadas.',
  metaWindowCustomMessage: 'Mensagem personalizada',
  metaWindowTimingTitle: 'Horários e retentativas',
  metaWindowLeadLabel: 'Primeira tentativa (minutos antes)',
  metaWindowAttemptsLabel: 'Quantidade de tentativas',
  metaWindowIntervalLabel: 'Intervalo entre tentativas (minutos)',
  metaWindowTimingHint:
    'Use qualquer quantidade de minutos e tentativas dentro dos limites operacionais. O exemplo 3 × 5 é apenas uma sugestão inicial.',
  metaWindowSchedulePreview: 'As tentativas serão feitas aproximadamente em:',
  metaWindowLastAttempt:
    'Última tentativa: {minutes} minutos antes do encerramento.',
  metaWindowSave: 'Salvar configuração',
  metaWindowSaving: 'Salvando...',
  metaWindowSaved: 'Configuração da janela salva.',
  metaWindowLoadError: 'Não foi possível carregar a configuração da janela.',
  metaWindowSaveError: 'Não foi possível salvar a configuração da janela.',
  metaWindowLoading: 'Carregando configuração...',
  metaWindowSelectedMessage: 'Mensagem selecionada:',
  metaWindowNoMessage: 'nenhuma',
  advancedChecklist: [
    'Use esta aba somente para ações administrativas sensíveis.',
    'Verifique dependências da equipe antes de excluir o grupo.',
    'Prefira remover canais e usuários primeiro quando estiver reorganizando a operação.',
  ],
  channelsTitle: 'Canais de atendimento',
  channelsDescription: 'Esta equipe atende conversas dos seguintes canais',
  sharedChannelsTitle: 'Canais compartilhados com esta equipe',
  sharedChannelsDescription:
    'São visíveis aqui, mas continuam roteados pela equipe de origem.',
  channelStatuses: {
    connected: 'Conectado',
    disconnected: 'Desconectado',
    connecting: 'Conectando',
    unknown: 'Não confirmado',
    linked: 'Vinculado',
  },
  addChannel: 'Adicionar canal',
  additionalChannelsTitle: 'Canais adicionais',
  channelSearchPlaceholder: 'Pesquisar canais...',
  channelSelectedLabel: 'Selecionado',
  channelAddLabel: 'Clique para adicionar',
  channelNoResults: 'Nenhum canal encontrado.',
  channelCollapseLabel: 'Clique para recolher',
  distributionTitle: 'Distribuição dos leads',
  distributionExplanation:
    'Escolha como o sistema deve decidir o responsável quando uma nova conversa entrar por um canal desta equipe.',
  distributionTeamTitle: 'Respeitar equipe',
  distributionTeamDescription:
    'A carteira só será respeitada quando o responsável pertencer a esta equipe.',
  distributionAttendantTitle: 'Respeitar atendente',
  distributionAttendantDescription:
    'O lead será enviado ao responsável carteirizado, mesmo que ele pertença a outra equipe.',
  accessTitle: 'Acesso aos atendimentos',
  accessDescription: 'Define quem poderá visualizar os atendimentos.',
  accessExplanation:
    'Esta escolha afeta principalmente privacidade, supervisão e histórico disponível para outros usuários.',
  accessAllTitle: 'Todos',
  accessAllDescription:
    'Membros da equipe veem em Outros, e todos os usuários do sistema veem em Concluídos.',
  accessTeamTitle: 'Equipe',
  accessTeamDescription: 'Visível apenas aos usuários desta equipe.',
  accessAttendantTitle: 'Atendente (por trecho)',
  accessAttendantDescription:
    'Cada atendente vê somente o trecho que atendeu; supervisores acompanham toda a conversa.',
  individualCompanyStartTitle: 'Uso de Corporativa Individual',
  individualCompanyStartDescription:
    'Define se os membros podem iniciar novas conversas usando uma Instância Corporativa Individual de outro membro.',
  individualCompanyStartAllowed: 'Permitido',
  individualCompanyStartAllowedDescription:
    'Membros da equipe podem iniciar e disparar novas mensagens por essas instâncias.',
  individualCompanyStartBlocked: 'Não permitido',
  individualCompanyStartBlockedDescription:
    'Somente o responsável pela instância pode iniciar novas conversas. Transferências continuam liberadas.',
  attendantsTitle: 'Usuários (atendentes)',
  attendantsExplanation:
    'Membros marcados para participar do rodízio podem receber conversas desta equipe conforme as regras de distribuição configuradas.',
  supervisorsTitle: 'Supervisores',
  supervisorsExplanation:
    'Supervisores acompanham a equipe e também podem atuar como atendentes quando participam do rodízio.',
  addUser: 'Gerenciar usuário',
  addSupervisor: 'Gerenciar supervisores',
  emptyAttendants: 'Nenhum atendente configurado.',
  emptySupervisors: 'Nenhum supervisor configurado.',
  emptyChannels: 'Nenhum canal conectado.',
  removeChannel: 'Remover canal',
  iaTitle: 'IA e Rodízios',
  iaDescription:
    'Ajuste a inteligência artificial, rodízios e horários desta equipe.',
  iaExplanation:
    'Use esta área quando precisar ajustar atendimento automático, critérios de rodízio ou regras de disponibilidade específicas desta equipe.',
  iaButton: 'Gerenciar IA e Rodízios',
  advancedTitle: 'Ações avançadas',
  advancedDescription:
    'Aqui ficam ações que alteram a estrutura da equipe. Use com cuidado e apenas quando a equipe não for mais necessária.',
  dangerTitle: 'Zona de perigo',
  dangerDeleteWarning:
    'Excluir uma equipe removerá todas as configurações, dados e associações.',
  dangerConfirmationLabel:
    'Confirmo que revisei os canais, usuários e regras desta equipe.',
  dangerConfirmationHint: 'É necessário confirmar para habilitar a exclusão.',
  dangerIrreversible: 'Esta ação não pode ser desfeita',
  deleteGroup: 'Excluir equipe',
};

const normalizeReminderData = data => {
  reminderPresets.value = data?.presets || {};
  reminderSettings.value = {
    ...reminderSettings.value,
    ...data,
    custom_messages: Array.from(
      { length: data?.custom_message_limit || 3 },
      (_, index) => data?.custom_messages?.[index] || ''
    ),
  };
};

const loadMetaWindowReminderSettings = async () => {
  if (!props.team?.id) return;

  reminderLoading.value = true;
  try {
    const { data } = await equipesApi.getMetaWindowReminders(props.team.id);
    normalizeReminderData(data);
  } catch (error) {
    useAlert(error?.response?.data?.error || workspaceText.metaWindowLoadError);
  } finally {
    reminderLoading.value = false;
  }
};

const reminderPresetOptions = computed(() =>
  Object.entries(reminderPresets.value).map(([key, message]) => ({
    key,
    label: reminderPresetLabels[key] || key,
    message,
  }))
);

const selectedReminderMessage = computed(() => {
  if (reminderSettings.value.message_source === 'custom') {
    return (
      reminderSettings.value.custom_messages[
        reminderSettings.value.custom_message_index
      ] || ''
    );
  }
  return reminderPresets.value[reminderSettings.value.preset_key] || '';
});

const lastReminderAttemptLead = computed(
  () =>
    Number(reminderSettings.value.lead_minutes || 0) -
    (Number(reminderSettings.value.attempts || 1) - 1) *
      Number(reminderSettings.value.interval_minutes || 0)
);

const reminderHasInvalidTiming = computed(
  () =>
    lastReminderAttemptLead.value < 5 ||
    Number(reminderSettings.value.interval_minutes || 0) < 1
);

const saveMetaWindowReminderSettings = async () => {
  if (reminderHasInvalidTiming.value || !selectedReminderMessage.value.trim()) {
    useAlert(
      'Revise a mensagem e mantenha a última tentativa pelo menos 5 minutos antes do encerramento.'
    );
    return;
  }

  reminderSaving.value = true;
  try {
    const { data } = await equipesApi.updateMetaWindowReminders(
      props.team.id,
      reminderSettings.value
    );
    normalizeReminderData(data);
    useAlert(workspaceText.metaWindowSaved);
  } catch (error) {
    useAlert(error?.response?.data?.error || workspaceText.metaWindowSaveError);
  } finally {
    reminderSaving.value = false;
  }
};

watch(
  () => props.team?.id,
  () => {
    selectedInboxIds.value = (props.team?.inboxes || []).map(inbox => inbox.id);
    showChannelPicker.value = false;
    channelSearchQuery.value = '';
    activeTab.value = 'channels';
    advancedDeleteConfirmed.value = false;
    loadMetaWindowReminderSettings();
  },
  { immediate: true }
);

watch(
  () => (props.team?.inboxes || []).map(inbox => inbox.id).join(','),
  () => {
    selectedInboxIds.value = (props.team?.inboxes || []).map(inbox => inbox.id);
  }
);

// Mantém os IDs arquivados na seleção para preservar seus vínculos ao salvar.
const channelTags = computed(() => visibleTeamInboxes(props.team?.inboxes));
const sharedChannelTags = computed(() =>
  visibleTeamInboxes(props.team?.shared_inboxes)
);
const channelStatusClass = inbox => {
  const status = teamInboxStatus(inbox);
  if (status === 'connected') return 'bg-[#DCFCE7] text-[#15803D]';
  if (status === 'disconnected') return 'bg-[#FEE2E2] text-[#B91C1C]';
  if (status === 'connecting') return 'bg-[#FEF3C7] text-[#92400E]';
  return 'bg-[#F1F5F9] text-[#475569]';
};
const channelOriginDescription = inbox => {
  const origin = inbox.origin_team?.name || props.team?.name || 'Não informada';
  const manager = inbox.original_manager?.name;

  return manager
    ? `Origem: ${origin} · Gestor original: ${manager}`
    : `Origem: ${origin}`;
};
const normalizedChannelSearchQuery = computed(() =>
  channelSearchQuery.value.trim().toLocaleLowerCase()
);
const filteredInboxOptions = computed(() => {
  if (!normalizedChannelSearchQuery.value) {
    return props.inboxOptions;
  }

  // Número digitado com máscara ("+55 45 98432-7137") casa com os dígitos.
  const digits = normalizedChannelSearchQuery.value.replace(/\D/g, '');
  return props.inboxOptions.filter(option => {
    const haystack = String(
      option.searchText || option.label || ''
    ).toLocaleLowerCase();
    return (
      haystack.includes(normalizedChannelSearchQuery.value) ||
      (digits.length >= 4 && haystack.includes(digits))
    );
  });
});
const attendants = computed(() =>
  (props.members || []).filter(isTeamRotationEnabled)
);
const supervisors = computed(() =>
  (props.members || []).filter(isTeamSupervisor)
);

const tabs = computed(() => [
  {
    key: 'channels',
    label: workspaceText.channelsTab,
    description: workspaceText.channelsStepperDescription,
    icon: 'i-lucide-store',
    status: channelTags.value.length
      ? workspaceText.configuredLabel
      : workspaceText.pendingLabel,
    complete: channelTags.value.length > 0,
  },
  {
    key: 'rules',
    label: workspaceText.rulesTab,
    description: workspaceText.rulesStepperDescription,
    icon: 'i-lucide-shuffle',
    status: workspaceText.configuredLabel,
    complete: Boolean(props.team?.distribution_mode && props.accessLevel),
  },
  {
    key: 'people',
    label: workspaceText.peopleTab,
    description: workspaceText.peopleStepperDescription,
    icon: 'i-lucide-users-round',
    status: props.members.length
      ? workspaceText.configuredLabel
      : workspaceText.pendingLabel,
    complete: props.members.length > 0,
  },
  {
    key: 'automation',
    label: workspaceText.automationTab,
    description: workspaceText.automationStepperDescription,
    icon: 'i-lucide-bot',
    status: workspaceText.reviewLabel,
    complete: false,
    review: true,
  },
  {
    key: 'meta_window',
    label: workspaceText.metaWindowTab,
    description: workspaceText.metaWindowStepperDescription,
    icon: 'i-lucide-message-circle-warning',
    status: reminderSettings.value.enabled
      ? workspaceText.configuredLabel
      : workspaceText.pendingLabel,
    complete: reminderSettings.value.enabled,
  },
  {
    key: 'advanced',
    label: workspaceText.advancedTab,
    description: workspaceText.advancedStepperDescription,
    icon: 'i-lucide-settings',
    status: workspaceText.reviewLabel,
    complete: false,
    review: true,
  },
]);

const activeTabIndex = computed(() =>
  tabs.value.findIndex(tab => tab.key === activeTab.value)
);
const activeTabData = computed(() => tabs.value[activeTabIndex.value]);
const tabProgressWidth = index => {
  const progressSteps = Math.max(tabs.value.length - 1, 1);
  return `${Math.min(((index + 1) / progressSteps) * 100, 100)}%`;
};
const activeTabDescription = computed(() => {
  if (activeTab.value === 'channels') {
    return workspaceText.channelsTabDescription;
  }
  if (activeTab.value === 'rules') return workspaceText.rulesTabDescription;
  if (activeTab.value === 'people') return workspaceText.peopleTabDescription;
  if (activeTab.value === 'automation') {
    return workspaceText.automationTabDescription;
  }
  if (activeTab.value === 'meta_window') {
    return workspaceText.metaWindowTabDescription;
  }
  return workspaceText.advancedTabDescription;
});

const goToNextTab = () => {
  const nextTab = tabs.value[activeTabIndex.value + 1];
  if (nextTab) activeTab.value = nextTab.key;
};

const openIaPanel = () => {
  emit('openIa', props.team);
};

const distributionCards = computed(() => [
  {
    key: 'respect_team',
    title: workspaceText.distributionTeamTitle,
    description: workspaceText.distributionTeamDescription,
  },
  {
    key: 'respect_attendant',
    title: workspaceText.distributionAttendantTitle,
    description: workspaceText.distributionAttendantDescription,
  },
]);

const accessCards = computed(() => [
  {
    key: 'all',
    title: workspaceText.accessAllTitle,
    description: workspaceText.accessAllDescription,
    icon: 'i-lucide-users',
  },
  {
    key: 'team',
    title: workspaceText.accessTeamTitle,
    description: workspaceText.accessTeamDescription,
    icon: 'i-lucide-users-round',
  },
  {
    key: 'attendant',
    title: workspaceText.accessAttendantTitle,
    description: workspaceText.accessAttendantDescription,
    icon: 'i-lucide-user-round',
  },
]);

const individualCompanyStartCards = computed(() => [
  // Tarefa #3553 — Validar regras de equipes, instâncias e carteirização no Zoraxy.
  // Esta escolha afeta novas conversas, nunca transferências.
  {
    value: true,
    title: workspaceText.individualCompanyStartAllowed,
    description: workspaceText.individualCompanyStartAllowedDescription,
  },
  {
    value: false,
    title: workspaceText.individualCompanyStartBlocked,
    description: workspaceText.individualCompanyStartBlockedDescription,
  },
]);

const initials = name =>
  (name || '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map(part => part[0]?.toUpperCase() || '')
    .join('');

const updateInboxes = ids => {
  selectedInboxIds.value = ids;
  emit('updateInboxes', ids);
};

const openChannelPicker = () => {
  if (!showChannelPicker.value) {
    showChannelPicker.value = true;
    channelSearchQuery.value = '';
  }
};

const closeChannelPicker = () => {
  showChannelPicker.value = false;
};

const toggleInbox = inboxId => {
  const nextInboxIds = selectedInboxIds.value.includes(inboxId)
    ? selectedInboxIds.value.filter(id => id !== inboxId)
    : [...selectedInboxIds.value, inboxId];

  updateInboxes(nextInboxIds);
};

const removeInbox = inboxId => {
  updateInboxes(selectedInboxIds.value.filter(id => id !== inboxId));
};
</script>

<template>
  <div
    class="teams-page__workspace flex min-w-0 flex-1 flex-col gap-6 rounded-[8px] border border-n-weak bg-white p-6 shadow-[0_12px_40px_rgba(15,23,42,0.05)] max-md:gap-4 max-md:p-4"
  >
    <div
      class="teams-page__workspace-heading flex items-center gap-4 max-md:items-start max-md:gap-3"
    >
      <span
        class="teams-page__workspace-heading-icon inline-flex size-14 flex-shrink-0 items-center justify-center rounded-full bg-[#e8f7ee] text-[#16a34a]"
        aria-hidden="true"
      >
        <span class="i-lucide-users-round size-7" />
      </span>
      <div class="min-w-0">
        <h2
          class="teams-page__workspace-heading-title mb-0 text-[22px] font-semibold leading-7 text-n-slate-12"
        >
          {{ workspaceText.setupTitle }}
        </h2>
        <p
          class="teams-page__workspace-heading-subtitle mb-0 mt-1 truncate text-sm leading-6 text-n-slate-11 max-md:whitespace-normal"
        >
          {{ teamMetaLine || workspaceText.setupDescription }}
        </p>
      </div>
    </div>

    <div
      class="teams-page__workspace-tabs-scroll scrollbar-thin -mx-1 overflow-x-auto px-1 pb-1"
      role="tablist"
      :aria-label="workspaceText.tabsAriaLabel"
    >
      <div
        class="teams-page__workspace-tabs-grid grid min-w-[1152px] grid-cols-6 gap-2 max-md:flex max-md:min-w-max"
      >
        <button
          v-for="(tab, index) in tabs"
          :id="`team-tab-${tab.key}`"
          :key="tab.key"
          type="button"
          role="tab"
          :aria-selected="activeTab === tab.key"
          :aria-controls="`team-panel-${tab.key}`"
          class="teams-page__workspace-tab relative flex h-[152px] min-w-0 flex-col items-start rounded-[14px] border px-4 py-3.5 text-left transition-colors max-md:h-auto max-md:min-h-36 max-md:w-48 max-md:px-4 max-md:py-4"
          :class="
            activeTab === tab.key
              ? 'teams-page__workspace-tab--active border-[#BCEBD4] bg-[#F1FBF6]'
              : 'teams-page__workspace-tab--idle border-transparent bg-transparent hover:bg-[#F8FAFC]'
          "
          @click="activeTab = tab.key"
        >
          <span
            class="teams-page__workspace-tab-icon relative inline-flex size-12 flex-shrink-0 items-center justify-center rounded-full border border-[#E2E8F0] bg-white shadow-[0_1px_3px_rgba(15,23,42,0.08)] max-md:size-14"
            :class="
              activeTab === tab.key
                ? 'teams-page__workspace-tab-icon--active text-[#166B50]'
                : 'teams-page__workspace-tab-icon--idle text-[#334155]'
            "
          >
            <span :class="tab.icon" class="size-6 max-md:size-7" />
            <span
              class="teams-page__workspace-tab-number absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-[22px] items-center justify-center rounded-full border-2 border-white px-1 text-[11px] font-bold leading-none shadow-[0_1px_2px_rgba(15,23,42,0.12)]"
              :class="
                activeTab === tab.key
                  ? 'bg-[#18A66A] text-white'
                  : 'bg-[#E2E8F0] text-[#475569]'
              "
              aria-hidden="true"
            >
              {{ String(index + 1).padStart(2, '0') }}
            </span>
            <span
              v-if="tab.complete"
              class="teams-page__workspace-tab-complete absolute -left-1.5 -top-1.5 inline-flex size-5 items-center justify-center rounded-full border-2 border-white bg-[#18A66A] text-white shadow-[0_1px_2px_rgba(15,23,42,0.12)]"
              aria-hidden="true"
            >
              <span class="i-lucide-check size-3" />
            </span>
          </span>
          <span
            class="teams-page__workspace-tab-copy mt-2 flex w-full min-w-0 max-w-[160px] flex-col"
          >
            <span
              class="teams-page__workspace-tab-label block text-[15px] font-semibold leading-5 max-md:text-[17px] max-md:leading-6"
              :class="
                activeTab === tab.key ? 'text-[#18A66A]' : 'text-[#1E293B]'
              "
            >
              {{ tab.label }}
            </span>
            <span
              class="teams-page__workspace-tab-description mt-1 text-[13px] leading-4 text-[#64748B] max-md:text-sm max-md:leading-5"
            >
              <span v-for="line in tab.description" :key="line" class="block">
                {{ line }}
              </span>
            </span>
          </span>
          <span
            v-if="activeTab === tab.key && index < tabs.length - 1"
            class="teams-page__workspace-tab-progress absolute bottom-2 left-4 right-4 mt-3 h-[3px] overflow-hidden rounded-full bg-[#D7F1E4]"
            aria-hidden="true"
          >
            <span
              class="teams-page__workspace-tab-progress-value block h-full rounded-full bg-[#18A66A]"
              :style="{ width: tabProgressWidth(index) }"
            />
          </span>
          <span
            v-if="index < tabs.length - 1"
            class="teams-page__workspace-tab-arrow pointer-events-none absolute right-[-14px] top-[38px] z-10 inline-flex size-5 items-center justify-center bg-white text-[#475569] max-md:top-[44px]"
            aria-hidden="true"
          >
            <span class="i-lucide-chevron-right size-4" />
          </span>
        </button>
      </div>
    </div>

    <div
      class="teams-page__workspace-current-step flex items-center justify-between gap-4 border-b border-n-weak pb-5 max-md:flex-col max-md:items-start"
    >
      <div class="teams-page__workspace-current-step-copy min-w-0">
        <p
          class="teams-page__workspace-current-step-eyebrow mb-1 text-xs font-semibold uppercase tracking-wide text-[#16a34a]"
        >
          {{ workspaceText.stepLabel }} {{ activeTabIndex + 1 }}
          {{ workspaceText.ofLabel }}
          {{ tabs.length }}
        </p>
        <h2
          class="teams-page__workspace-current-step-title m-0 text-xl font-semibold text-n-slate-12"
        >
          {{ activeTabData.label }}
        </h2>
        <p
          class="teams-page__workspace-current-step-description mb-0 mt-1 text-sm leading-6 text-n-slate-11"
        >
          {{ activeTabDescription }}
        </p>
      </div>
    </div>

    <section
      v-if="activeTab === 'channels'"
      id="team-panel-channels"
      role="tabpanel"
      aria-labelledby="team-tab-channels"
      class="teams-page__workspace-panel teams-page__workspace-panel--channels teams-page__channel-panel overflow-hidden rounded-[16px] border border-n-weak bg-white"
    >
      <button
        type="button"
        class="teams-page__channel-panel-header flex min-h-14 w-full items-center justify-between gap-4 border-b border-[#F1F3F5] bg-white px-5 py-3 text-left transition-colors duration-200 hover:bg-[#fcfcfd]"
        @click="openChannelPicker"
      >
        <div class="flex min-w-0 flex-col">
          <div class="flex min-w-0 items-center gap-3">
            <h3 class="m-0 text-[18px] font-semibold leading-6 text-[#1F2937]">
              {{ workspaceText.channelsTitle }}
            </h3>
            <div
              class="inline-flex h-6 flex-shrink-0 items-center rounded-full bg-[#F3F4F6] px-2.5 text-[13px] font-medium text-[#6B7280]"
            >
              {{ channelTags.length }}
            </div>
          </div>
          <p class="mb-0 mt-0.5 text-sm leading-5 text-[#6B7280]">
            {{ workspaceText.channelsDescription }}
          </p>
        </div>
      </button>

      <div class="teams-page__channel-panel-body p-5 max-md:p-4">
        <div
          v-if="channelTags.length"
          class="teams-page__channel-list mb-4 flex flex-col gap-3"
        >
          <div
            v-for="inbox in channelTags"
            :key="inbox.id"
            class="teams-page__channel-card flex min-h-[64px] items-center justify-between gap-4 rounded-[14px] border border-[#BFE7C7] bg-[#F3FBF5] px-4 py-3 text-sm text-n-slate-12 max-md:flex-wrap max-md:gap-3"
          >
            <div
              class="teams-page__channel-card-main flex min-w-0 flex-1 items-center gap-3 max-md:basis-full"
            >
              <span
                class="teams-page__channel-card-icon inline-flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-white shadow-[0_4px_12px_rgba(15,23,42,0.10)]"
                :class="channelIconColor(inbox)"
              >
                <span :class="channelIcon(inbox)" class="size-5" />
              </span>
              <span class="min-w-0">
                <span
                  class="teams-page__channel-card-name block truncate text-sm font-semibold text-[#14532D]"
                >
                  {{ inbox.name }}
                </span>
                <span
                  v-if="inbox.origin_team || inbox.original_manager"
                  class="mt-0.5 block truncate text-xs text-[#4B7860]"
                >
                  {{ channelOriginDescription(inbox) }}
                </span>
              </span>
            </div>
            <span
              class="teams-page__channel-card-status inline-flex flex-shrink-0 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold"
              :class="channelStatusClass(inbox)"
            >
              <span
                class="size-1.5 rounded-full bg-current"
                aria-hidden="true"
              />
              {{ workspaceText.channelStatuses[teamInboxStatus(inbox)] }}
            </span>
            <button
              type="button"
              class="inline-flex size-10 flex-shrink-0 items-center justify-center rounded-full text-n-slate-10 transition-colors hover:bg-white hover:text-n-ruby-11"
              :aria-label="workspaceText.removeChannel"
              @click.stop="removeInbox(inbox.id)"
            >
              <span class="i-lucide-x size-5" />
            </button>
          </div>
        </div>

        <div
          v-if="sharedChannelTags.length"
          class="mb-4 rounded-[16px] border border-[#BFE7C7] bg-[#F6FCF8] p-4"
        >
          <div class="mb-3 flex items-center justify-between gap-3">
            <div>
              <h4 class="m-0 text-sm font-semibold text-[#14532D]">
                {{ workspaceText.sharedChannelsTitle }}
              </h4>
              <p class="mb-0 mt-1 text-xs text-[#4B7860]">
                {{ workspaceText.sharedChannelsDescription }}
              </p>
            </div>
            <span
              class="rounded-full bg-[#DCFCE7] px-2.5 py-1 text-xs font-semibold text-[#15803D]"
            >
              {{ sharedChannelTags.length }}
            </span>
          </div>
          <div class="grid gap-2 sm:grid-cols-2">
            <article
              v-for="inbox in sharedChannelTags"
              :key="inbox.id"
              class="rounded-xl border border-[#D6EEDD] bg-white p-3"
            >
              <p class="m-0 truncate text-sm font-semibold text-[#1F2937]">
                {{ inbox.name }}
              </p>
              <p class="mb-0 mt-1 text-xs leading-5 text-[#64748B]">
                {{ channelOriginDescription(inbox) }}
              </p>
            </article>
          </div>
        </div>

        <button
          v-if="!showChannelPicker"
          type="button"
          class="teams-page__channel-picker-summary group w-full rounded-[16px] border border-[#E5E7EB] bg-white p-4 transition-colors hover:border-[#BBF7D0] hover:bg-[#F4FBF5]"
          @click="openChannelPicker"
        >
          <div
            class="teams-page__channel-picker-summary-header flex items-center justify-between gap-3"
          >
            <span class="flex min-w-0 items-center gap-3 text-left">
              <span
                class="inline-flex size-10 flex-shrink-0 items-center justify-center rounded-full bg-[#E8F7EE] text-[#16A34A]"
              >
                <span class="i-lucide-plus size-5" />
              </span>
              <span class="min-w-0">
                <span
                  class="teams-page__channel-picker-summary-title block truncate text-base font-semibold text-[#111827]"
                >
                  {{ workspaceText.additionalChannelsTitle }}
                </span>
                <span class="mt-0.5 block text-sm text-[#6B7280]">
                  {{ workspaceText.addChannel }}
                </span>
              </span>
            </span>
            <span
              class="inline-flex size-9 flex-shrink-0 items-center justify-center rounded-full bg-[#F8FAFC] text-[#16A34A] transition-colors group-hover:bg-[#E8F7EE]"
              aria-hidden="true"
            >
              <span class="i-lucide-chevron-down size-5" />
            </span>
          </div>
        </button>

        <div
          v-else
          class="teams-page__channel-expanded flex flex-col gap-4 rounded-[16px] border border-[#D9DEE5] bg-[#FCFCFD] p-4"
        >
          <div class="relative">
            <span
              class="teams-page__channel-search-icon pointer-events-none i-lucide-search absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-n-slate-10"
            />
            <input
              v-model="channelSearchQuery"
              type="search"
              :placeholder="workspaceText.channelSearchPlaceholder"
              class="teams-page__channel-search block h-11 min-h-11 w-full rounded-full border-none border-0 bg-[#f5f7fb] py-2 pl-10 pr-4 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak transition-all duration-500 ease-in-out placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand disabled:cursor-not-allowed disabled:opacity-50 dark:outline-n-weak dark:placeholder:text-n-slate-10 dark:hover:outline-n-slate-6 dark:focus:outline-n-brand [appearance:textfield] file:border-0 file:bg-transparent file:text-sm file:font-medium [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none"
            />
          </div>

          <div
            class="teams-page__channel-options flex max-h-[18rem] flex-col gap-2 overflow-y-auto pr-1"
          >
            <label
              v-for="option in filteredInboxOptions"
              :key="option.value"
              class="teams-page__channel-option flex cursor-pointer items-center gap-3 rounded-[12px] border border-[#E5E7EB] bg-white px-4 py-3 transition-all duration-200 hover:-translate-y-0.5 hover:border-[#BBF7D0] hover:bg-[#F4FBF5] hover:shadow-[0_4px_16px_rgba(0,0,0,0.04)]"
            >
              <input
                :checked="selectedInboxIds.includes(option.value)"
                type="checkbox"
                class="size-4 rounded border-[#CBD5E1] accent-[#22C55E] focus:ring-[#86EFAC]"
                @change="toggleInbox(option.value)"
              />
              <span class="teams-page__channel-option-copy min-w-0 flex-1">
                <span
                  class="teams-page__channel-option-label block truncate text-sm font-medium text-[#111827]"
                >
                  {{ option.label }}
                </span>
              </span>
            </label>

            <p
              v-if="filteredInboxOptions.length === 0"
              class="teams-page__channel-option-empty mb-0 px-1 py-2 text-sm text-[#6B7280]"
            >
              {{ workspaceText.channelNoResults }}
            </p>
          </div>

          <button
            type="button"
            class="self-center text-sm font-medium text-[#16A34A] transition-colors hover:underline"
            @click="closeChannelPicker"
          >
            <span class="inline-flex items-center gap-1">
              <span>{{ workspaceText.channelCollapseLabel }}</span>
              <span class="i-lucide-chevron-up size-4" />
            </span>
          </button>
        </div>

        <div
          class="teams-page__channel-tip mt-4 flex items-start gap-3 rounded-[12px] bg-[#F4FBF5] px-4 py-3.5"
        >
          <span
            class="i-lucide-info mt-0.5 size-4 flex-shrink-0 text-[#16A34A]"
            aria-hidden="true"
          />
          <p class="mb-0 text-sm leading-5 text-[#4B7860]">
            <span class="font-semibold text-[#14532D]">
              {{ workspaceText.tipLabel }}
            </span>
            {{ workspaceText.tipChannels }}
          </p>
        </div>
      </div>
    </section>

    <div
      v-if="activeTab === 'rules'"
      id="team-panel-rules"
      role="tabpanel"
      aria-labelledby="team-tab-rules"
      class="teams-page__workspace-panel teams-page__workspace-panel--rules flex flex-col gap-7"
    >
      <section
        class="teams-page__rules-section teams-page__rules-section--distribution flex flex-col gap-4"
      >
        <div class="teams-page__rules-section-header">
          <h3
            class="teams-page__rules-section-title m-0 text-base font-semibold text-n-slate-12"
          >
            {{ workspaceText.distributionTitle }}
          </h3>
          <p
            class="teams-page__rules-section-description mb-0 mt-1 text-sm leading-6 text-n-slate-11"
          >
            {{ workspaceText.distributionExplanation }}
          </p>
        </div>
        <div class="teams-page__distribution-options grid gap-3 lg:grid-cols-2">
          <button
            v-for="card in distributionCards"
            :key="card.key"
            type="button"
            class="teams-page__distribution-option flex rounded-[8px] border border-transparent px-5 py-5 text-left transition-colors max-md:p-4"
            :class="
              team.distribution_mode === card.key
                ? 'bg-[#f3fbf5] shadow-[inset_0_0_0_1px_#22c55e]'
                : 'bg-[#f5f7fb] shadow-[inset_0_0_0_1px_#dbe1ea] hover:shadow-[inset_0_0_0_1px_#cfd7e3]'
            "
            @click="$emit('updateDistributionMode', card.key)"
          >
            <div class="flex items-start gap-4">
              <span
                class="mt-1 inline-flex size-5 min-h-5 min-w-5 flex-shrink-0 rounded-full border-2"
                :class="
                  team.distribution_mode === card.key
                    ? 'border-[#22c55e] bg-[#22c55e] shadow-[inset_0_0_0_4px_white]'
                    : 'border-[#c7d0dc] bg-white'
                "
              />
              <div>
                <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                  {{ card.title }}
                </h4>
                <p class="mb-0 mt-2 text-sm leading-6 text-n-slate-11">
                  {{ card.description }}
                </p>
              </div>
            </div>
          </button>
        </div>
      </section>

      <section
        class="teams-page__rules-section teams-page__rules-section--access flex flex-col gap-4"
      >
        <div class="teams-page__rules-section-header">
          <h3
            class="teams-page__rules-section-title m-0 text-base font-semibold text-n-slate-12"
          >
            {{ workspaceText.accessTitle }}
          </h3>
          <p
            class="teams-page__rules-section-description mb-0 mt-1 text-sm leading-6 text-n-slate-11"
          >
            {{ workspaceText.accessDescription }}
            {{ workspaceText.accessExplanation }}
          </p>
        </div>
        <div class="teams-page__access-options grid gap-3 xl:grid-cols-3">
          <button
            v-for="card in accessCards"
            :key="card.key"
            type="button"
            class="teams-page__access-option flex rounded-[8px] border border-transparent px-5 py-5 text-left transition-colors max-md:p-4"
            :class="
              accessLevel === card.key
                ? 'bg-[#f3fbf5] shadow-[inset_0_0_0_1px_#22c55e]'
                : 'bg-[#f5f7fb] shadow-[inset_0_0_0_1px_#dbe1ea] hover:shadow-[inset_0_0_0_1px_#cfd7e3]'
            "
            @click="$emit('updateAccessLevel', card.key)"
          >
            <div class="flex flex-col gap-3">
              <div class="flex items-center gap-3">
                <span
                  class="inline-flex size-5 min-h-5 min-w-5 flex-shrink-0 rounded-full border-2"
                  :class="
                    accessLevel === card.key
                      ? 'border-[#22c55e] bg-[#22c55e] shadow-[inset_0_0_0_4px_white]'
                      : 'border-[#c7d0dc] bg-white'
                  "
                />
                <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                  {{ card.title }}
                </h4>
              </div>
              <div>
                <p class="mb-0 text-sm leading-6 text-n-slate-11">
                  {{ card.description }}
                </p>
              </div>
            </div>
          </button>
        </div>
      </section>

      <section
        class="teams-page__rules-section teams-page__rules-section--individual-company flex flex-col gap-4"
      >
        <div class="teams-page__rules-section-header">
          <h3 class="m-0 text-base font-semibold text-n-slate-12">
            {{ workspaceText.individualCompanyStartTitle }}
          </h3>
          <p class="mb-0 mt-1 text-sm leading-6 text-n-slate-11">
            {{ workspaceText.individualCompanyStartDescription }}
          </p>
        </div>
        <div class="grid gap-3 lg:grid-cols-2">
          <button
            v-for="card in individualCompanyStartCards"
            :key="String(card.value)"
            type="button"
            class="flex rounded-[8px] border border-transparent px-5 py-5 text-left transition-colors max-md:p-4"
            :class="
              team.allow_member_individual_company_start === card.value
                ? 'bg-[#f3fbf5] shadow-[inset_0_0_0_1px_#22c55e]'
                : 'bg-[#f5f7fb] shadow-[inset_0_0_0_1px_#dbe1ea] hover:shadow-[inset_0_0_0_1px_#cfd7e3]'
            "
            @click="$emit('updateIndividualCompanyStart', card.value)"
          >
            <div class="flex items-start gap-4">
              <span
                class="mt-1 inline-flex size-5 min-h-5 min-w-5 flex-shrink-0 rounded-full border-2"
                :class="
                  team.allow_member_individual_company_start === card.value
                    ? 'border-[#22c55e] bg-[#22c55e] shadow-[inset_0_0_0_4px_white]'
                    : 'border-[#c7d0dc] bg-white'
                "
              />
              <div>
                <h4 class="m-0 text-sm font-semibold text-n-slate-12">
                  {{ card.title }}
                </h4>
                <p class="mb-0 mt-2 text-sm leading-6 text-n-slate-11">
                  {{ card.description }}
                </p>
              </div>
            </div>
          </button>
        </div>
      </section>
    </div>

    <section
      v-if="activeTab === 'people'"
      id="team-panel-people"
      role="tabpanel"
      aria-labelledby="team-tab-people"
      class="teams-page__workspace-panel teams-page__workspace-panel--people grid gap-6 xl:grid-cols-2"
    >
      <div
        class="teams-page__people-card teams-page__people-card--attendants flex flex-col gap-4 rounded-3xl border border-n-weak bg-[#fbfcfd] p-5 max-md:p-4"
      >
        <div class="teams-page__people-card-header">
          <h3
            class="teams-page__people-card-title m-0 text-base font-semibold text-n-slate-12"
          >
            {{ workspaceText.attendantsTitle }}
          </h3>
          <p
            class="teams-page__people-card-description mb-0 mt-1 text-sm leading-6 text-n-slate-11"
          >
            {{ workspaceText.attendantsExplanation }}
          </p>
        </div>
        <div
          v-if="attendants.length"
          class="teams-page__people-list teams-page__people-list--attendants flex flex-wrap gap-2.5"
        >
          <div
            v-for="member in attendants"
            :key="member.id"
            class="teams-page__people-chip teams-page__people-chip--attendant inline-flex items-center gap-2 rounded-full border border-[#d7e6da] bg-white px-2.5 py-2"
          >
            <span
              class="teams-page__people-chip-avatar inline-flex size-8 items-center justify-center rounded-full bg-[#16a34a] text-xs font-semibold text-white"
            >
              {{ initials(member.name) }}
            </span>
            <span
              class="teams-page__people-chip-name text-sm font-medium text-n-slate-12"
            >
              {{ member.name }}
            </span>
          </div>
        </div>
        <p v-else class="teams-page__people-empty mb-0 text-sm text-n-slate-11">
          {{ workspaceText.emptyAttendants }}
        </p>
        <Button
          outline
          slate
          size="sm"
          :label="workspaceText.addUser"
          class="w-fit !rounded-full"
          @click="$emit('openMembers')"
        />
      </div>

      <div
        class="teams-page__people-card teams-page__people-card--supervisors flex flex-col gap-4 rounded-3xl border border-n-weak bg-[#fbfcfd] p-5 max-md:p-4"
      >
        <div class="teams-page__people-card-header">
          <h3
            class="teams-page__people-card-title m-0 text-base font-semibold text-n-slate-12"
          >
            {{ workspaceText.supervisorsTitle }}
          </h3>
          <p
            class="teams-page__people-card-description mb-0 mt-1 text-sm leading-6 text-n-slate-11"
          >
            {{ workspaceText.supervisorsExplanation }}
          </p>
        </div>
        <div
          v-if="supervisors.length"
          class="teams-page__people-list teams-page__people-list--supervisors flex flex-wrap gap-2.5"
        >
          <div
            v-for="member in supervisors"
            :key="member.id"
            class="teams-page__people-chip teams-page__people-chip--supervisor inline-flex items-center gap-2 rounded-full border border-[#d7e6da] bg-white px-2.5 py-2"
          >
            <span
              class="teams-page__people-chip-avatar inline-flex size-8 items-center justify-center rounded-full bg-[#16a34a] text-xs font-semibold text-white"
            >
              {{ initials(member.name) }}
            </span>
            <span
              class="teams-page__people-chip-name text-sm font-medium text-n-slate-12"
            >
              {{ member.name }}
            </span>
          </div>
        </div>
        <p v-else class="teams-page__people-empty mb-0 text-sm text-n-slate-11">
          {{ workspaceText.emptySupervisors }}
        </p>
        <Button
          outline
          slate
          size="sm"
          :label="workspaceText.addSupervisor"
          class="w-fit !rounded-full"
          @click="$emit('openMembers')"
        />
      </div>
    </section>

    <section
      v-if="activeTab === 'automation'"
      id="team-panel-automation"
      role="tabpanel"
      aria-labelledby="team-tab-automation"
      class="teams-page__workspace-panel teams-page__workspace-panel--automation rounded-3xl border border-[#dceee1] bg-[#f6fbf7] p-5"
    >
      <div
        class="teams-page__automation-content flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
      >
        <div class="teams-page__automation-copy">
          <h3
            class="teams-page__automation-title m-0 text-base font-semibold text-n-slate-12"
          >
            {{ workspaceText.iaTitle }}
          </h3>
          <p
            class="teams-page__automation-description mb-0 mt-1 text-sm text-n-slate-11"
          >
            {{ workspaceText.iaDescription }}
          </p>
          <p
            class="teams-page__automation-explanation mb-0 mt-2 text-sm leading-6 text-n-slate-11"
          >
            {{ workspaceText.iaExplanation }}
          </p>
        </div>
        <button
          type="button"
          class="teams-page__automation-ia-button inline-flex min-h-8 min-w-0 items-center justify-center gap-2 rounded-full border-0 bg-n-brand px-3 py-1 text-sm text-white outline-1 outline-transparent transition-all duration-100 ease-out hover:enabled:brightness-110 focus-visible:brightness-110 active:enabled:scale-[0.97] disabled:opacity-50 max-md:w-full"
          @click.stop.prevent="openIaPanel"
        >
          <span class="i-lucide-bot size-4" aria-hidden="true" />
          <span class="min-w-0 whitespace-normal break-words">{{ workspaceText.iaButton }}</span>
        </button>
      </div>
    </section>

    <section
      v-if="activeTab === 'advanced'"
      id="team-panel-advanced"
      role="tabpanel"
      aria-labelledby="team-tab-advanced"
      class="teams-page__workspace-panel teams-page__workspace-panel--advanced rounded-[18px] bg-[#FFF6F7] p-7 max-md:p-4"
    >
      <div class="teams-page__advanced-content flex flex-col gap-7">
        <div
          class="teams-page__advanced-copy flex items-start gap-5 max-md:gap-3"
        >
          <span
            class="teams-page__advanced-alert-icon i-lucide-triangle-alert mt-1 size-12 flex-shrink-0 text-[#DC2F3F] max-md:size-8"
            aria-hidden="true"
          />
          <div class="min-w-0">
            <h3
              class="teams-page__advanced-title m-0 text-lg font-semibold text-[#DC2F3F]"
            >
              {{ workspaceText.dangerTitle }}
            </h3>
            <p
              class="teams-page__advanced-description mb-0 mt-2 text-[15px] leading-6 text-[#475569]"
            >
              {{ workspaceText.dangerDeleteWarning }}
            </p>
          </div>
        </div>

        <div class="h-px bg-[#F4C8CC]" aria-hidden="true" />

        <label
          class="teams-page__advanced-confirmation flex cursor-pointer items-start gap-4 rounded-[14px] bg-[#FFF0F1] px-5 py-4"
        >
          <input
            v-model="advancedDeleteConfirmed"
            type="checkbox"
            class="mt-1 size-5 rounded border-2 border-[#EF9AA4] text-[#DC2F3F] accent-[#DC2F3F] focus:ring-[#FCA5AD]"
          />
          <span class="min-w-0">
            <span
              class="teams-page__advanced-confirmation-label block text-[15px] font-semibold leading-6 text-[#1F2937]"
            >
              {{ workspaceText.dangerConfirmationLabel }}
            </span>
            <span
              class="teams-page__advanced-confirmation-hint mt-0.5 block text-sm leading-5 text-[#94A3B8]"
            >
              {{ workspaceText.dangerConfirmationHint }}
            </span>
          </span>
        </label>

        <div
          class="teams-page__advanced-actions flex flex-col items-center gap-3"
        >
          <button
            type="button"
            :disabled="!advancedDeleteConfirmed"
            class="teams-page__advanced-delete-button inline-flex min-w-[320px] items-center justify-center gap-3 rounded-[10px] bg-[#D62F3A] px-8 py-4 text-base font-semibold text-white transition-colors hover:bg-[#BE2732] disabled:cursor-not-allowed disabled:bg-[#E9A3AA] max-md:w-full max-md:min-w-0 max-md:px-4"
            @click="$emit('delete')"
          >
            <span class="i-lucide-trash-2 size-5" />
            {{ workspaceText.deleteGroup }}
          </button>
          <p
            class="teams-page__advanced-irreversible mb-0 text-sm font-semibold text-[#E0525D]"
          >
            {{ workspaceText.dangerIrreversible }}
          </p>
        </div>
      </div>
    </section>

    <section
      v-if="activeTab === 'meta_window'"
      id="team-panel-meta_window"
      role="tabpanel"
      aria-labelledby="team-tab-meta_window"
      class="teams-page__workspace-panel teams-page__workspace-panel--meta-window flex flex-col gap-6"
    >
      <div>
        <h3 class="m-0 text-base font-semibold text-n-slate-12">
          {{ workspaceText.metaWindowTitle }}
        </h3>
        <p class="mb-0 mt-1 text-sm leading-6 text-n-slate-11">
          {{ workspaceText.metaWindowDescription }}
        </p>
      </div>

      <div
        class="flex items-start gap-3 rounded-[14px] border border-[#F59E0B] bg-[#FFFBEB] p-4 text-[#92400E]"
        role="alert"
      >
        <span
          class="i-lucide-triangle-alert mt-0.5 size-5 flex-shrink-0"
          aria-hidden="true"
        />
        <div class="text-sm leading-6">
          <strong class="block">{{
            workspaceText.metaWindowWarningTitle
          }}</strong>
          <span>{{ workspaceText.metaWindowWarning }}</span>
        </div>
      </div>

      <label
        class="flex cursor-pointer items-center gap-3 rounded-[12px] border border-n-weak bg-white px-4 py-3 text-sm font-semibold text-n-slate-12"
      >
        <input
          v-model="reminderSettings.enabled"
          type="checkbox"
          class="size-4 rounded border-[#CBD5E1] accent-[#22C55E] focus:ring-[#86EFAC]"
        />
        {{ workspaceText.metaWindowEnable }}
      </label>

      <div class="flex flex-col gap-3">
        <h4 class="m-0 text-sm font-semibold text-n-slate-12">
          {{ workspaceText.metaWindowMessageTitle }}
        </h4>
        <div
          class="flex cursor-pointer items-start gap-3 rounded-[12px] border border-n-weak bg-white p-4"
        >
          <input
            v-model="reminderSettings.message_source"
            value="preset"
            type="radio"
            class="mt-1 size-4 accent-[#22C55E]"
          />
          <span class="flex min-w-0 flex-1 flex-col gap-3">
            <span class="text-sm font-medium text-n-slate-12">{{
              workspaceText.metaWindowPresetOption
            }}</span>
            <span class="grid gap-2 md:grid-cols-2">
              <label
                v-for="option in reminderPresetOptions"
                :key="option.key"
                class="flex cursor-pointer items-start gap-2 rounded-lg border border-[#E2E8F0] p-3 text-xs leading-5 text-n-slate-11"
                :class="
                  reminderSettings.preset_key === option.key
                    ? 'border-[#86EFAC] bg-[#F0FDF4]'
                    : ''
                "
              >
                <input
                  v-model="reminderSettings.preset_key"
                  :value="option.key"
                  type="radio"
                  class="mt-0.5 size-3.5 accent-[#22C55E]"
                />
                <span>
                  <strong class="block text-n-slate-12">{{
                    option.label
                  }}</strong>
                  {{ option.message }}
                </span>
              </label>
            </span>
          </span>
        </div>

        <div
          class="flex cursor-pointer items-start gap-3 rounded-[12px] border border-n-weak bg-white p-4"
        >
          <input
            v-model="reminderSettings.message_source"
            value="custom"
            type="radio"
            class="mt-1 size-4 accent-[#22C55E]"
          />
          <span class="flex min-w-0 flex-1 flex-col gap-3">
            <span>
              <span class="block text-sm font-medium text-n-slate-12">{{
                workspaceText.metaWindowCustomOption
              }}</span>
              <span class="mt-1 block text-xs text-n-slate-11">{{
                workspaceText.metaWindowCustomHint
              }}</span>
            </span>
            <span class="flex flex-col gap-2">
              <label
                v-for="(message, index) in reminderSettings.custom_messages"
                :key="index"
                class="flex cursor-pointer items-start gap-2"
              >
                <input
                  v-model="reminderSettings.custom_message_index"
                  :value="index"
                  type="radio"
                  class="mt-3 size-3.5 accent-[#22C55E]"
                />
                <span class="flex min-w-0 flex-1 flex-col gap-1">
                  <span class="text-xs font-medium text-n-slate-11">
                    {{ workspaceText.metaWindowCustomMessage }} {{ index + 1 }}
                  </span>
                  <textarea
                    v-model="reminderSettings.custom_messages[index]"
                    rows="2"
                    maxlength="1000"
                    class="w-full resize-y rounded-lg border border-solid border-n-weak bg-white px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-n-brand"
                  />
                </span>
              </label>
            </span>
          </span>
        </div>
      </div>

      <div
        class="flex flex-col gap-3 rounded-[14px] border border-n-weak bg-[#F8FAFC] p-4"
      >
        <div>
          <h4 class="m-0 text-sm font-semibold text-n-slate-12">
            {{ workspaceText.metaWindowTimingTitle }}
          </h4>
          <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
            {{ workspaceText.metaWindowSchedulePreview }}
          </p>
          <p class="mb-0 mt-1 text-xs leading-5 text-n-slate-11">
            {{ workspaceText.metaWindowTimingHint }}
          </p>
        </div>
        <div class="grid gap-4 md:grid-cols-3">
          <label
            class="flex flex-col gap-1 text-xs font-medium text-n-slate-11"
          >
            {{ workspaceText.metaWindowLeadLabel }}
            <input
              v-model.number="reminderSettings.lead_minutes"
              type="number"
              min="5"
              class="h-10 rounded-lg border border-n-weak bg-white px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-xs font-medium text-n-slate-11"
          >
            {{ workspaceText.metaWindowAttemptsLabel }}
            <input
              v-model.number="reminderSettings.attempts"
              type="number"
              min="1"
              max="20"
              class="h-10 rounded-lg border border-n-weak bg-white px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
          <label
            class="flex flex-col gap-1 text-xs font-medium text-n-slate-11"
          >
            {{ workspaceText.metaWindowIntervalLabel }}
            <input
              v-model.number="reminderSettings.interval_minutes"
              type="number"
              min="1"
              class="h-10 rounded-lg border border-n-weak bg-white px-3 text-sm text-n-slate-12 outline-none focus:border-n-brand"
            />
          </label>
        </div>
        <p
          class="mb-0 text-xs font-medium"
          :class="
            reminderHasInvalidTiming ? 'text-[#B91C1C]' : 'text-n-slate-11'
          "
        >
          {{
            workspaceText.metaWindowLastAttempt.replace(
              '{minutes}',
              lastReminderAttemptLead
            )
          }}
        </p>
      </div>

      <div
        class="flex items-center justify-between gap-3 max-md:flex-col max-md:items-stretch"
      >
        <span v-if="reminderLoading" class="text-xs text-n-slate-11">
          {{ workspaceText.metaWindowLoading }}
        </span>
        <span v-else class="text-sm text-n-slate-11">
          {{ workspaceText.metaWindowSelectedMessage }}
          {{ selectedReminderMessage || workspaceText.metaWindowNoMessage }}
        </span>
        <Button
          type="button"
          :is-loading="reminderSaving"
          :disabled="reminderLoading || reminderSaving"
          class="max-md:w-full !rounded-full"
          @click="saveMetaWindowReminderSettings"
        >
          {{
            reminderSaving
              ? workspaceText.metaWindowSaving
              : workspaceText.metaWindowSave
          }}
        </Button>
      </div>
    </section>

    <div
      v-if="activeTabIndex < tabs.length - 1"
      class="teams-page__workspace-footer flex justify-end pt-1 max-md:[&>button]:w-full"
    >
      <Button
        size="sm"
        icon="i-lucide-arrow-right"
        trailing-icon
        :label="workspaceText.nextStep"
        class="!h-11 !rounded-full !bg-[#166B50] !px-6 !text-sm !font-semibold !text-white shadow-[0_8px_18px_rgba(22,107,80,0.18)] transition-all hover:!bg-[#125840]"
        @click="goToNextTab"
      />
    </div>
  </div>
</template>

<style scoped>
.teams-page__channel-search {
  height: 44px !important;
  min-height: 44px !important;
  line-height: 44px !important;
  background: rgba(194, 189, 184, 0.15) !important;
}
</style>
